<template>
  <Modal :is-open="is_modal_shown">
    <template #form>
      <transition
        mode="out-in"
        enter-active-class="transition duration-300 ease-out transform"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0">
        <div v-if="!add_new_field_is_shown">
          <h3 class="text-2xl font-medium leading-10 text-gray-900 capitalize dark:text-white">
            <span>{{ $t("Add new") }} <span class="capitalize">{{ object_type }}</span></span>
          </h3>
          <hr class="mt-2 -mx-6 border-light-divider-border dark:border-dark-divider-border">

          <Form
            v-slot="{ isSubmitting }"
            :validation-schema="schema"
            class="space-y-6 overflow-visible min-h-[250px] flex flex-col"
            @submit="createRecord">
            <div class="mb-auto -space-y-px rounded-md">
              <div v-if="record_metrics.length > 0" class="p-3 mt-5 rounded-sm">
                <div class="grid w-full h-full grid-cols-1 gap-2 sm:grid-cols-2">
                  <RecordAutoCompleteInput
                    v-if="object_type != 'company'"
                    :filter-metric-ids="[$utils.getMetricByReferenceName($utils.getOptionMetricReference('company', 'name')).id]"
                    :is-async="true"
                    :placeholder="``"
                    :label="object_type == 'company' ? 'Parent Company' : 'Company'"
                    :is-box-background="true"
                    :disabled="
                      $route.name == 'CompanyProfile' || ($route.name == 'ObjectProfile' && $route.params.object_type == 'opportunity')
                    "
                    type="text"
                    label-attr="name"
                    classes="w-full block border-none p-0"
                    :value="parent_company"
                    @chosen="handleSelectParentCompany" />

                  <div v-for="metric in record_metrics" :key="metric.id" class="mb-2">
                    <MetricFieldInput v-model:metric-value="metric.value" :metric="metric" :record="null" class="w-full" />
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-end px-3">
              <div class="primary-outline-btn w-fit" @click="add_new_field_is_shown = true">
                <Plus class="w-5 h-5" />
                <div class="pl-1">
                  {{ $t("Add Metric") }}
                </div>
              </div>
            </div>

            <div class="flex flex-row justify-between w-full gap-2 pt-2">
              <button type="button" class="w-1/2 m-0 cancel-btn dark:border-dark-divider-border" @click="is_modal_shown = false">
                {{ $t("Cancel") }}
              </button>

              <button type="submit" :disabled="isSubmitting || is_submitting" class="relative w-1/2 primary-btn disabled:bg-opacity-50">
                {{ $t("Add") + ` ${$utils.ObjectType(object_type).label}` }}
              </button>
            </div>
          </Form>
        </div>
        <div v-else>
          <h3 class="text-2xl font-medium leading-10 text-center text-gray-800 dark:text-white">
            {{ $t("New Field") }}
          </h3>
          <NewFieldForm
            v-model:included-metrics="record_metrics"
            :inside-modal="inside_modal"
            :page-reference="page_reference"
            :object-type="object_type"
            :view-type="VIEW_TYPES.FORM_CREATE"
            :component-reference="component_reference"
            @cancel="
              () => {
                prepareValidationSchema()
                add_new_field_is_shown = false
              }
            "
            @done="
              () => {
                prepareValidationSchema()
                add_new_field_is_shown = false
              }
            " />
        </div>
      </transition>
    </template>
  </Modal>
</template>
<script>
import * as Yup from "yup"
import Modal from "@/components/shared/Modal.vue"
import dataService from "@/services/data.service"
import RecordAutoCompleteInput from "@/components/shared/RecordAutoCompleteInput.vue"
import { Form } from "vee-validate"
import NewFieldForm from "@/components/forms/NewFieldForm.vue"
import MetricFieldInput from "@/components/shared/MetricFieldInput.vue"
import { Plus } from "lucide-vue-next"
import { VIEW_TYPES } from "@/store/constants/references"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Plus,
    MetricFieldInput,
    NewFieldForm,
    Form,
    RecordAutoCompleteInput,
    Modal
  },
  inject: ["getViewMetrics"],

  emits: ["updateMap", "refreshTable"],

  data() {
    return {
      VIEW_TYPES,
      page_reference: null,
      object_type: null,
      view_type: null,
      component_reference: null,
      layout_page_reference: null,
      parent_company: null,
      add_new_field_is_shown: false,
      record_metrics: [],
      is_modal_shown: false,
      is_submitting: false,
      is_form_valid: false,
      schema: {},
      view_metrics: [],
      inside_modal: false
    }
  },

  watch: {
    parent_company() {
      if (!this.parent_company) {
        this.is_form_valid = false
      }
    },
    is_modal_shown() {
      if (!this.is_modal_shown) {
        this.parent_company = null
      }
    },
    view_metrics() {
      if (this.view_metrics) {
        this.record_metrics = this.view_metrics?.map((metric) => {
          return {
            ...metric,
            value: this.getMetricValue(metric.reference_name),
            is_checked: true
          }
        })
      }
    }
  },

  created() {
    this.register_listeners()

    this.view_metrics = this.getViewMetrics({
      page_reference: this.page_reference,
      object_type: this.object_type,
      view_type: VIEW_TYPES.FORM_CREATE,
      flat: false,
      layout_page_reference: this.layout_page_reference,
      component_reference: this.component_reference
    })
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_NEW_RECORD_FORM, () => {
        this.view_metrics = this.getViewMetrics({
          page_reference: this.page_reference,
          object_type: this.object_type,
          view_type: VIEW_TYPES.FORM_CREATE,
          flat: false,
          layout_page_reference: this.layout_page_reference,
          component_reference: this.component_reference
        })
      })

      this.$emitter.on(
        ALL_EMITTERS.ADD_NEW_RECORD,
        ({
          page_reference,
          object_type,
          parent_company,
          view_type,
          layout_page_reference,
          component_reference,
          inside_modal,
          defaults = null
        }) => {
          //Adding a child record in company profile or opportunity profile
          if (parent_company) {
            this.parent_company = parent_company
          } else if (
            this.$route.name == "CompanyProfile" ||
            (this.$route.name == "ObjectProfile" && this.$route.params.object_type == "opportunity")
          ) {
            this.parent_company = this.$store.state.ui.currentCompany
          }
          if (this.object_type != object_type) {
            this.record_metrics = []
          }
          this.inside_modal = inside_modal
          this.object_type = object_type
          this.page_reference = page_reference
          this.view_type = view_type
          this.layout_page_reference = layout_page_reference
          this.component_reference = component_reference

          this.view_metrics = this.getViewMetrics({
            page_reference: this.page_reference,
            object_type: this.object_type,
            view_type: this.view_type,
            flat: false,
            layout_page_reference: this.layout_page_reference,
            component_reference: this.component_reference
          })
          this.setObjectMetrics()
          if (defaults) {
            this.record_metrics = this.record_metrics.map((metric) => {
              if (defaults[metric.reference_name]) {
                metric.value = defaults[metric.reference_name]
              }
              return metric
            })
          }

          this.is_modal_shown = true
        }
      )
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_NEW_RECORD_FORM)
      this.$emitter.off(ALL_EMITTERS.ADD_NEW_RECORD)
    },

    handleSelectParentCompany(e) {
      this.parent_company = e
    },
    prepareValidationSchema() {
      this.schema = {}
      let required_metrics = [
        this.$utils.getOptionMetricReference("company", "website"),
        this.$utils.getOptionMetricReference("company", "name"),
        this.$utils.getOptionMetricReference("contact", "first_name"),
        this.$utils.getOptionMetricReference("contact", "last_name"),
        this.$utils.getOptionMetricReference("contact", "email"),
        this.$utils.getOptionMetricReference("department", "name"),
        this.$utils.getOptionMetricReference("opportunity", "name"),
        this.$utils.getOptionMetricReference("opportunity", "amount")
      ]

      this.record_metrics?.forEach((metric) => {
        if (required_metrics.includes(metric.reference_name)) {
          if (metric.input_type == "URL") {
            this.schema[metric.reference_name] = Yup.string()
              .matches(
                /^(http[s]?:\/\/(www\.)?|ftp:\/\/(www\.)?|www\.){0,1}([0-9A-Za-z-\\.@:%_\\+~#=]+)+((\.[a-zA-Z]{2,3})+)(\/(.)*)?(\?(.)*)?/,
                this.$t("Please enter a valid url address")
              )
              .required(`${metric.name} ` + this.$t("is required"))
          } else if (metric.data_type == "Number") {
            this.schema[metric.reference_name] = Yup.number(this.$t("Please enter a valid number")).required(
              `${metric.name} ` + this.$t("is required")
            )
          } else if (metric.input_type == "Email") {
            this.schema[metric.reference_name] = Yup.string().email(this.$t("Please enter a valid email address"))
          } else {
            this.schema[metric.reference_name] = Yup.string().required(`${metric.name} ` + this.$t("is required"))
          }
        } else {
          if (metric.input_type == "URL") {
            this.schema[metric.reference_name] = Yup.string()
              .matches(
                /^(http[s]?:\/\/(www\.)?|ftp:\/\/(www\.)?|www\.){0,1}([0-9A-Za-z-\\.@:%_\\+~#=]+)+((\.[a-zA-Z]{2,3})+)(\/(.)*)?(\?(.)*)?|^$/,
                this.$t("Please enter a valid url address")
              )
              .nullable()
          } else if (metric.input_type == "Email") {
            this.schema[metric.reference_name] = Yup.string().email(this.$t("Please enter a valid email address")).nullable()
          } else {
            this.schema[metric.reference_name] = Yup.string().nullable()
          }
        }
      })
    },

    setObjectMetrics() {
      this.record_metrics = this.view_metrics?.map((metric) => {
        return {
          ...metric,
          value: this.getMetricValue(metric.reference_name),
          is_checked: true
        }
      })
      this.prepareValidationSchema()
    },
    getMetricValue(reference_name) {
      let value = this.$utils.find(this.record_metrics, {
        reference_name: reference_name
      })?.value
      if (value == undefined) {
        return null
      }
      return value
    },

    createRecord() {
      this.is_submitting = true
      this.createCompany()
        .then(({ data }) => {
          this.$emitter.emit(ALL_EMITTERS.RECORD_ADDED, data)
          this.is_modal_shown = false
          this.is_form_valid = false
          this.record_metrics = []
          this.is_submitting = false

          this.$toast.open({
            message:
              this.$t("The") + ` ${this.$store.state.object_types.find((m) => m.id == data.object_type).type} ` + this.$t("was created"),
            position: "top",
            type: "success",
            duration: 3000
          })
        })
        .catch(() => {
          this.is_submitting = false
        })
    },
    createCompany() {
      return new Promise((res) => {
        let metrics_values = {}
        this.record_metrics?.forEach((metric) => {
          metrics_values[metric.reference_name] = metric.value
        })
        return res(
          dataService.createRecord({
            values: metrics_values,
            object_id: "RANDOM",
            object_type: this.$utils.getObjectTypeByType(this.object_type).id,
            type: this.$utils.getObjectTypeByType(this.object_type).type,
            parent: this.parent_company?.id,
            origin: "platform-user"
          })
        )
      })
    }
  }
}
</script>
